import React, { useRef } from 'react';
import { PaymentInfoPopover } from '@features/payments/components/PaymentInfoText';
import { usePatientPaymentMethods } from '@features/payments/store/hooks';
import { useSubjectLanguage } from '@features/user';
import PaymentIcon from '@mui/icons-material/Payment';
import { Box, Collapse, Typography } from '@mui/material';
import { useSessionReplayCapture } from '@services/statsig';
import { capitalize } from '@utils/utils';

import { useCardExpansion } from '../../store/hooks';
import {
  type ActionCommand,
  ActionCommandType,
  type BaseAction,
  type CardContentProps,
  type PaymentRequestPayload,
} from '../../types';
import { PaymentRequestFormWrapper } from '../forms';

import CardContent from './base/CardContent';

interface PaymentRequestCardContentProps extends CardContentProps {
  action: BaseAction<'payment_request'>;
}

const PaymentRequestCardContent: React.FC<PaymentRequestCardContentProps> = ({
  action,
  onCommand,
}) => {
  const { isExpanded } = useCardExpansion();
  const { startCapture } = useSessionReplayCapture();
  const expanded = isExpanded(action.action_id);
  const formRef = useRef<HTMLFormElement>(null);
  const hasHoveredRef = useRef(false);
  const subjectLanguage = useSubjectLanguage();
  const SubjectLanguage = capitalize(subjectLanguage);
  const latestEditedPayloadRef = useRef<PaymentRequestPayload | null>(null);

  const paymentAction = action;
  const payload = paymentAction.action_payload;

  // Check if patient has payment method on file
  const { paymentMethods } = usePatientPaymentMethods(paymentAction.patient?.patient_id ?? '');
  const hasPaymentMethod = paymentMethods.length > 0;

  const formatAmount = (amount: string | null) => {
    if (!amount) return 'N/A';
    // Assuming amount is already formatted with dollar sign
    return amount.startsWith('$') ? amount : `$${amount}`;
  };

  const content = (
    <>
      {paymentAction.patient?.name && (
        <Typography variant="body2" color="text.secondary">
          {paymentAction.patient.name}
        </Typography>
      )}

      <Collapse in={!expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            pb: 1.5,
            alignItems: 'flex-start',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Amount: {formatAmount(payload.amount)}
          </Typography>
          <PaymentInfoPopover />
        </Box>
      </Collapse>
    </>
  );

  const formatAppointmentDate = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const expandableContent = (
    <Box sx={{ px: 2, pb: 2 }}>
      {payload.appointment_date && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Appointment date: {formatAppointmentDate(payload.appointment_date)}
        </Typography>
      )}
      <PaymentRequestFormWrapper
        actionPayload={paymentAction.action_payload}
        patientId={paymentAction.patient?.patient_id}
        patientName={paymentAction.patient?.name || undefined}
        encounterId={paymentAction.encounter_id}
        actionId={paymentAction.action_id}
        isLoading={false}
        enableDraft={true}
        showDueDate={!hasPaymentMethod}
        ref={formRef}
        onSubmit={updatedPayload => {
          // Store the updated payload in ref for immediate access
          latestEditedPayloadRef.current = updatedPayload;

          // Send the accept command with edits
          onCommand({
            type: ActionCommandType.ACCEPT_WITH_EDITS,
            payload: updatedPayload,
          });
        }}
      />
    </Box>
  );

  // Title changes based on whether patient has payment method on file
  const cardTitle = hasPaymentMethod
    ? `Charge ${SubjectLanguage}'s Payment Method?`
    : `Request Payment From ${SubjectLanguage}?`;

  return (
    <CardContent
      id={action.action_id}
      title={cardTitle}
      content={content}
      avatarIcon={<PaymentIcon />}
      onMouseOver={() => {
        if (!hasHoveredRef.current) {
          hasHoveredRef.current = true;
          startCapture('payment_request_action_card_hover');
        }
      }}
      onCommand={(command: ActionCommand) => {
        if (command.type === ActionCommandType.ACCEPT) {
          if (expanded) {
            // Trigger form submission using the form ref
            formRef.current?.requestSubmit();
          } else {
            onCommand({ type: ActionCommandType.ACCEPT });
          }
        } else if (command.type === ActionCommandType.EDIT) {
          onCommand(command);
        } else if (command.type === ActionCommandType.CANCEL_EDIT) {
          onCommand(command);
        } else {
          onCommand(command);
        }
      }}
      expandable={true}
      expandableContent={expandableContent}
      isExpandableContentEditable={true}
    />
  );
};

export default PaymentRequestCardContent;
